import * as THREE from 'three';

/**
 * Sun direction shared by the sky dome, the ocean's specular sun and the
 * directional light, so the sun disc, water glints and ship shadows all agree.
 *
 * Kept low in the sky (24 degrees above the horizon) for the *Black Sails*
 * mood: long shadows, a warm rim on the cloud deck and a glitter path on the
 * sea rather than a flat high-noon wash. The warm disc is suppressed as the
 * storm builds (see `stormGate` in `SKY_FRAGMENT`).
 */
export const SUN_DIRECTION = new THREE.Vector3().setFromSphericalCoords(
  1,
  THREE.MathUtils.degToRad(66),
  THREE.MathUtils.degToRad(140),
);

/** Scene-graph name used by `StormFx` to drive the sky's storm/flash uniforms. */
export const SKY_DOME_NAME = 'sky-dome';

/** Half-extent of the visible sky box; kept well inside the camera far plane. */
const SKY_DOME_SCALE = 1200;
/** Scene-graph name of the ocean water (owned by `OceanSystem`). */
const OCEAN_WATER_NAME = 'ocean-water';

/** Calm horizon/zenith palette (also the aerial-perspective fog base). */
const HORIZON_COLOR = new THREE.Color('#8fa4b4');
const ZENITH_COLOR = new THREE.Color('#33506b');
/** Full-storm horizon/zenith palette. */
const HORIZON_STORM = new THREE.Color('#151d28');
const ZENITH_STORM = new THREE.Color('#070b12');
/** Warm rim/sun-shaft accent — the one warm light left in the storm. */
const RIM_COLOR = new THREE.Color('#d9a86a');
/** Storm value baked into the PBR environment map so reflections read moody. */
const ENV_STORM = 0.55;

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

type SkyUniforms = Record<string, THREE.IUniform>;

function makeSkyUniforms(mobile: boolean): SkyUniforms {
  return {
    uSunDir: { value: SUN_DIRECTION.clone().normalize() },
    uStorm: { value: 0 },
    uFlash: { value: 0 },
    uTime: { value: 0 },
    uOctaves: { value: mobile ? 3 : 4 },
    uHorizonColor: { value: HORIZON_COLOR.clone() },
    uHorizonStorm: { value: HORIZON_STORM.clone() },
    uZenithColor: { value: ZENITH_COLOR.clone() },
    uZenithStorm: { value: ZENITH_STORM.clone() },
    uRimColor: { value: RIM_COLOR.clone() },
    uCloudCoverage: { value: 0.5 },
    uCloudDensity: { value: 0.55 },
  };
}

/**
 * Deterministic layered storm sky. A single BackSide box follows the ocean so
 * it always surrounds the camera; the shader builds a horizon-to-zenith
 * gradient, a low crepuscular rim/sun shaft and three drifting cloud layers, all
 * driven toward a dark, dense deck as `uStorm` rises. The warm sun disc fades
 * out as the storm builds and thin cloud gaps become cold rifts of light.
 * `uFlash` (lightning) briefly brightens sky and clouds. No `Math.random` — the
 * cloud field is a pure value-noise fBm.
 */
const SKY_VERTEX = /* glsl */ `
  varying vec3 vWorldDir;
  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldDir = worldPos.xyz - cameraPosition;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position.z = gl_Position.w; // pin the dome to the far plane
  }
`;

const SKY_FRAGMENT = /* glsl */ `
  precision highp float;

  varying vec3 vWorldDir;

  uniform vec3 uSunDir;
  uniform float uStorm;
  uniform float uFlash;
  uniform float uTime;
  uniform float uOctaves;
  uniform vec3 uHorizonColor;
  uniform vec3 uHorizonStorm;
  uniform vec3 uZenithColor;
  uniform vec3 uZenithStorm;
  uniform vec3 uRimColor;
  uniform float uCloudCoverage;
  uniform float uCloudDensity;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  float fbm(vec2 p) {
    float value = 0.0;
    float amp = 0.5;
    float norm = 0.0;
    for (int i = 0; i < 5; i++) {
      if (float(i) >= uOctaves) break;
      value += amp * noise(p);
      norm += amp;
      p = p * 2.03 + vec2(1.7, 0.9);
      amp *= 0.5;
    }
    return value / max(norm, 1e-4);
  }

  void main() {
    vec3 dir = normalize(vWorldDir);
    float h = clamp(dir.y, 0.0, 1.0);

    // Horizon -> zenith gradient, deepening with storm. The storm zenith is
    // nearly black so the ceiling reads heavy, not just overcast.
    vec3 horizon = mix(uHorizonColor, uHorizonStorm, uStorm);
    vec3 zenith = mix(uZenithColor, uZenithStorm, uStorm);
    vec3 sky = mix(horizon, zenith, pow(h, 0.5));

    vec2 dirXZ = normalize(dir.xz + vec2(1e-5));
    vec2 sunXZ = normalize(uSunDir.xz + vec2(1e-5));
    float azimuth = clamp(dot(dirXZ, sunXZ), -1.0, 1.0);
    float sunDot = clamp(dot(dir, normalize(uSunDir)), -1.0, 1.0);

    // The warm low sun only lives in calm weather: a small bright disc plus a
    // wide crepuscular glow. stormGate fully suppresses both during a storm.
    float stormGate = 1.0 - smoothstep(0.25, 0.65, uStorm);
    float disc = smoothstep(0.9982, 0.9996, sunDot);
    float glow = pow(max(0.0, azimuth), 14.0) * exp(-pow((h - 0.05) / 0.22, 2.0));
    sky += uRimColor * (disc * 0.9 + glow * 1.2) * stormGate;

    // Heavy thunderhead ceiling: a low dense base deck, billowing mid towers
    // and a high broken anvil top, all driven toward a heavy dark mass as
    // uStorm rises. Thin gaps between the cells become cold rifts of light.
    if (h > 0.004) {
      float elevation = mix(1.0, 0.38, uStorm);
      vec2 plane = dir.xz / max(h * elevation, 0.004);
      float drift = uTime * 0.006;

      // ── Layer 1: the dark base deck ────────────────────────────────
      vec2 uvBase = plane * 1.25 + vec2(drift, drift * 0.7);
      float baseN = fbm(uvBase);
      float baseN2 = fbm(uvBase * 2.9 + vec2(6.0, 3.0));
      float baseBand = smoothstep(0.008, 0.12, h) * (1.0 - smoothstep(0.34, 0.52, h));
      float baseCoverage = mix(uCloudCoverage, 0.99, uStorm);
      float baseCloud = smoothstep(
        1.0 - baseCoverage,
        1.0 - baseCoverage + 0.38,
        baseN * 0.5 + baseN2 * 0.5
      );

      // ── Layer 2: towering cumulus cells ─────────────────────────────
      vec2 uvTower = plane * 1.65 - vec2(drift * 0.5, drift * 0.4);
      float towerN = fbm(uvTower);
      float towerN2 = fbm(uvTower * 2.4 + vec2(11.0, 5.0));
      float billow = pow(max(0.0, towerN), 1.6);
      float towerBand = smoothstep(0.1, 0.26, h) * (1.0 - smoothstep(0.54, 0.74, h));
      float towerCloud = smoothstep(
        0.55,
        0.95,
        towerN * 0.5 + towerN2 * 0.4 + billow * 0.4
      );

      // ── Layer 3: high broken anvil top ──────────────────────────────
      vec2 uvAnvil = plane * 0.85 + vec2(drift * 0.2, -drift * 0.25);
      float anvilN = fbm(uvAnvil);
      float anvilBand = smoothstep(0.38, 0.55, h) * (1.0 - smoothstep(0.78, 0.94, h));
      float anvilCloud = smoothstep(0.44, 0.82, anvilN);

      // Cloudier and denser as the storm builds; calm weather keeps a thin,
      // broken deck.
      float density = mix(uCloudDensity, 1.0, uStorm);
      float cloudMask = clamp(
        baseCloud * baseBand +
        towerCloud * towerBand * 0.95 +
        anvilCloud * anvilBand * 0.85 * (0.5 + 0.5 * uStorm),
        0.0,
        1.0
      ) * density;

      // Vertical shading: a pitch-dark underside and a cold steel top that
      // catches the dim storm light, so the thunderheads read as lit tops on a
      // heavy base instead of a flat dark gradient.
      float cloudShade = smoothstep(0.02, 0.6, h);
      vec3 baseCol = mix(vec3(0.018, 0.024, 0.038), vec3(0.13, 0.15, 0.21), baseN2);
      vec3 topCol = mix(vec3(0.09, 0.12, 0.19), vec3(0.34, 0.42, 0.58), anvilN);
      vec3 cloudCol = mix(baseCol, topCol, cloudShade);

      float sunFace = pow(max(0.0, azimuth), 3.0);
      // Warm sunlit rims stay only while the storm gate is open.
      cloudCol += uRimColor * sunFace * cloudShade * 0.14 * stormGate;
      // Cold silver lining along the tower edges deepens the storm silhouette.
      cloudCol += vec3(0.32, 0.4, 0.56) * sunFace * billow * towerBand * uStorm * 0.5;

      sky = mix(sky, cloudCol, cloudMask);

      // Rifts: cold shafts bleeding through the broken deck.
      float rift = (1.0 - smoothstep(0.42, 0.86, baseCloud));
      rift *= baseBand * smoothstep(0.06, 0.3, h) * (1.0 - smoothstep(0.85, 1.0, h));
      sky += vec3(0.36, 0.46, 0.66) * rift * uStorm * 0.75;

      // Wet haze hugging the horizon adds depth to the rain sheet.
      float haze = smoothstep(0.0, 0.06, h) * (1.0 - smoothstep(0.06, 0.2, h));
      sky = mix(sky, horizon * 0.9, haze * (0.3 + 0.4 * uStorm));
    }

    // Lightning: a cold white-blue lift across sky, clouds and rain.
    sky = mix(sky, sky + vec3(0.46, 0.52, 0.7), uFlash * 0.85);

    gl_FragColor = vec4(sky, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

/**
 * Owns the visible storm sky dome and the PBR environment map derived from it.
 *
 * The dome is a single BackSide box that follows the ocean each frame, so it
 * stays centred on the action regardless of where the ship sails. Its shader is
 * a deterministic layered cloud deck driven through `uStorm`/`uFlash` (by
 * `StormFx`) and `uTime` (here). A PMREM capture of a stormier sky-only scene
 * is exposed as `environment` so every `MeshStandardMaterial` reflects cold,
 * moody light instead of a clear-blue day.
 */
export class SkySystem {
  readonly sky: THREE.Mesh;
  readonly environment: THREE.Texture;

  private readonly scene: THREE.Scene;
  private readonly uniforms: SkyUniforms;
  private readonly envTarget: THREE.WebGLRenderTarget;
  private water: THREE.Object3D | null = null;
  private elapsed = 0;

  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer, mobile: boolean) {
    this.scene = scene;

    // Visible sky dome.
    this.uniforms = makeSkyUniforms(mobile);
    const material = new THREE.ShaderMaterial({
      name: 'StormSkyShader',
      uniforms: this.uniforms,
      vertexShader: SKY_VERTEX,
      fragmentShader: SKY_FRAGMENT,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
    this.sky = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), material);
    this.sky.name = SKY_DOME_NAME;
    this.sky.scale.setScalar(SKY_DOME_SCALE);
    this.sky.frustumCulled = false;
    this.sky.renderOrder = -10;
    scene.add(this.sky);

    // A solid horizon fallback behind the dome (never visible unless the dome
    // somehow misses a pixel); keeps the clear colour from going black.
    scene.background = HORIZON_COLOR.clone();

    // The ocean is constructed before this system, so its water can be found
    // here and followed on each update.
    this.water = scene.getObjectByName(OCEAN_WATER_NAME) ?? null;

    // Bake the environment map from a stormier, sky-only scene. A second dome
    // is used so the visible sky's storm/flash state is not disturbed.
    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileCubemapShader();

    const envUniforms = makeSkyUniforms(mobile);
    envUniforms.uStorm.value = ENV_STORM;
    envUniforms.uFlash.value = 0;
    const envMaterial = new THREE.ShaderMaterial({
      name: 'StormSkyEnvShader',
      uniforms: envUniforms,
      vertexShader: SKY_VERTEX,
      fragmentShader: SKY_FRAGMENT,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
    const envMesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), envMaterial);
    envMesh.scale.setScalar(SKY_DOME_SCALE);
    envMesh.frustumCulled = false;

    const envScene = new THREE.Scene();
    envScene.add(envMesh);
    this.envTarget = pmrem.fromScene(envScene, 0, 0.1, SKY_DOME_SCALE * 2);
    this.environment = this.envTarget.texture;

    envMesh.geometry.dispose();
    envMaterial.dispose();
    pmrem.dispose();
  }

  /** Set the storm intensity (0..1). Driven by `StormFx` each frame. */
  setStorm(storm: number): void {
    this.uniforms.uStorm.value = clamp01(storm);
  }

  /** Set the lightning flash (0..1). Driven by `StormFx` each frame. */
  setFlash(flash: number): void {
    this.uniforms.uFlash.value = clamp01(flash);
  }

  /**
   * Advance the cloud animation and keep the dome centred on the action.
   * `delta` is seconds; the cloud drift is a pure function of elapsed time so
   * repeated frames stay deterministic.
   */
  update(delta: number): void {
    this.elapsed += delta;
    this.uniforms.uTime.value = this.elapsed;

    if (this.water) {
      this.sky.position.set(this.water.position.x, 0, this.water.position.z);
    }
  }

  dispose(): void {
    this.scene.remove(this.sky);
    this.sky.geometry.dispose();
    (this.sky.material as THREE.Material).dispose();
    this.envTarget.dispose();
    this.environment.dispose();
    this.scene.background = null;
  }
}
